/**
 * Test page for ImagePicker component.
 * Navigate to /test-image-picker to use this page.
 */
import React, { useState } from 'react';
import { Button, Card } from '@/components/ui';
import { ImagePicker, AspectRatioFilter } from '@/components/media';
import { useMediaImages } from '@/hooks/queries/useMediaQueries';
import { getDisplayUrl } from '@/types/media';

export function TestImagePicker() {
  // ImagePicker state
  const [isOpen, setIsOpen] = useState(false);
  const [selectedImageIds, setSelectedImageIds] = useState<string[]>([]);

  // Test configuration
  const [maxSelection, setMaxSelection] = useState<number | undefined>(undefined);
  const [aspectRatioFilter, setAspectRatioFilter] = useState<AspectRatioFilter | undefined>(undefined);

  // Get selected images for display
  const { data: imagesResponse } = useMediaImages({ page_size: 100 });
  const selectedImages = (imagesResponse?.images || []).filter(
    (img) => selectedImageIds.includes(img.id)
  );

  const handleConfirm = (imageIds: string[]) => {
    setSelectedImageIds(imageIds);
    console.log('Selected image IDs:', imageIds);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">ImagePicker Test Page</h1>

      {/* Configuration Section */}
      <Card className="p-4 mb-6">
        <h2 className="text-lg font-semibold mb-4">Configuration</h2>

        <div className="grid grid-cols-2 gap-4 mb-4">
          {/* Max Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Max Selection
            </label>
            <select
              value={maxSelection || ''}
              onChange={(e) => setMaxSelection(e.target.value ? Number(e.target.value) : undefined)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">Unlimited</option>
              <option value="1">1 (Single select)</option>
              <option value="3">3</option>
              <option value="5">5</option>
              <option value="10">10</option>
            </select>
          </div>

          {/* Aspect Ratio Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Aspect Ratio Filter
            </label>
            <select
              value={aspectRatioFilter || ''}
              onChange={(e) => setAspectRatioFilter(e.target.value as AspectRatioFilter | undefined || undefined)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">None (All ratios)</option>
              <option value="1:1">Square (1:1)</option>
              <option value="1.91:1">Landscape (1.91:1)</option>
              <option value="16:9">Widescreen (16:9)</option>
              <option value="4:5">Portrait (4:5)</option>
            </select>
          </div>
        </div>

        <Button onClick={() => setIsOpen(true)}>
          Open ImagePicker
        </Button>
      </Card>

      {/* Selected Images Display */}
      <Card className="p-4">
        <h2 className="text-lg font-semibold mb-4">
          Selected Images ({selectedImageIds.length})
        </h2>

        {selectedImageIds.length === 0 ? (
          <p className="text-gray-500 text-sm">No images selected. Click the button above to open the ImagePicker.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mb-4">
              {selectedImages.map((image) => (
                <div key={image.id} className="border rounded-lg overflow-hidden bg-gray-50">
                  <div className="aspect-square bg-gray-100">
                    <img
                      src={getDisplayUrl(image)}
                      alt={image.alt_text || image.original_filename}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="p-2">
                    <p className="text-xs font-medium truncate">{image.original_filename}</p>
                    <p className="text-xs text-gray-500">{image.width}x{image.height}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t pt-4">
              <h3 className="text-sm font-medium mb-2">Selected IDs (for API integration):</h3>
              <pre className="bg-gray-100 p-2 rounded text-xs overflow-x-auto">
                {JSON.stringify(selectedImageIds, null, 2)}
              </pre>
            </div>

            <Button
              variant="secondary"
              size="sm"
              className="mt-4"
              onClick={() => setSelectedImageIds([])}
            >
              Clear Selection
            </Button>
          </>
        )}
      </Card>

      {/* ImagePicker Modal */}
      <ImagePicker
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        selectedImageIds={selectedImageIds}
        onConfirm={handleConfirm}
        maxSelection={maxSelection}
        aspectRatioFilter={aspectRatioFilter}
      />
    </div>
  );
}

export default TestImagePicker;
